import type { FC, ReactNode } from "react";
import { DDSButton, DDSHeadlineText, DDSBodyText } from "@web-monorepo/dds";
import { coerceError } from "@web-monorepo/utilities";
import styles from "./ErrorPage.module.css";
import errorBoundaryStyles from "./ReactRouterErrorBoundary.module.css";
import { ErrorMojo } from "./ErrorMojo";

export { ErrorMojo };
import { TranslatedString } from "@web-monorepo/i18next";
import { useSideEffectOnMount } from "@web-monorepo/hooks";
import { appBrokenHint, AppBrokenSource, captureAppBrokenMessage, captureException } from "@web-monorepo/telemetry";
import { autoTranslate } from "@web-monorepo/translation-runtime";
import { isApiResponseError, APIResponseError } from "@web-monorepo/fetchers";
import {
  ProgrammerReadableErrorType,
  getErrorConfig,
  errorTypeFromHttpStatus,
  HttpStatusCode,
  is307RequestBlocked,
  isModuleLoadError,
  isLoadSheddingError,
  isNoNetworkError,
  isEmptyResponseBodyError,
  isRawNetworkError,
} from "./errorConfig";

// Legacy type for backwards compatibility - maps HTTP status codes to error types
export type ErrorType = "default" | "404" | "401" | "403" | "307" | "500" | "429" | "400" | "503" | "504" | "502";

// Map legacy error types to new programmer-readable types
function legacyErrorTypeToNew(errorType: ErrorType): ProgrammerReadableErrorType {
  const mapping: Record<ErrorType, ProgrammerReadableErrorType> = {
    "307": "temporary-redirect",
    "400": "bad-request",
    "401": "not-allowed",
    "403": "forbidden",
    "404": "not-found",
    "429": "rate-limited",
    "500": "server-error",
    "502": "bad-gateway",
    "503": "service-unavailable",
    "504": "gateway-timeout",
    default: "default",
  };
  return mapping[errorType];
}
export interface ErrorPageProps {
  error?: unknown;
  errorType?: ErrorType;
  reloadButtonText?: ReactNode | TranslatedString;
  children?: ReactNode;
  noReloadButton?: boolean;
  reloadButtonHref?: string;
  // Sometimes we do want to show the stack trace in production. Specifically in admin site
  // where the user seeing the error is an internal employee and the error details are
  // valuable for us to fix their issues.
  showStackTraceInProduction?: boolean;
  // Which terminal surface this page represents, for the `app_broken` Sentry tag.
  // The default ("error_boundary") opts in automatically: every ErrorPage render
  // reports to Sentry unless the terminal state is HTTP-level (401/404/5xx…).
  // Pass null if this render is not a terminal user-facing failure surface, or when
  // a parent surface reports the event itself.
  appBrokenSource?: AppBrokenSource | null;
}

function resolveErrorType(error: unknown, legacyType?: ErrorType): ProgrammerReadableErrorType {
  const errorTypeFromErrorObj = error ? errorTypeFromError(coerceError(error)) : undefined;
  const legacyErrorType = legacyType ? legacyErrorTypeToNew(legacyType) : undefined;
  return legacyErrorType ?? errorTypeFromErrorObj ?? "default";
}

// Only client-side breakage gets the app_broken tag. HTTP-status pages (401/404/5xx…)
// are the API talking — already visible in server metrics and logClient — and would
// drown the tag in session expiries and bad links.
const APP_BROKEN_REPORTABLE: ReadonlySet<ProgrammerReadableErrorType> = new Set<ProgrammerReadableErrorType>([
  "default",
  "module-load-error",
  "no-network",
]);

function reportAppBroken(source: AppBrokenSource | null, error: unknown, errorType: ProgrammerReadableErrorType) {
  if (source === null) return;
  if (!APP_BROKEN_REPORTABLE.has(errorType)) {
    // HTTP-level terminal page: keep the pre-existing untagged capture for real
    // errors; errorType-only pages emit nothing.
    if (error) captureException(coerceError(error));
    return;
  }
  if (error) {
    // Error types whose default grouping is unstable across deploys get a stable fingerprint from
    // appBrokenHint itself; see STABLE_APP_BROKEN_FINGERPRINTS in @web-monorepo/telemetry.
    captureException(coerceError(error), appBrokenHint(source, { detail: errorType }));
  } else {
    // Terminal full-page state with no exception object to capture (e.g. a bare
    // <ErrorPage /> rendered for a failed fetch) — still report it.
    captureAppBrokenMessage(source, errorType);
  }
}

function handleReloadButtonClick(reloadButtonHref?: string) {
  if (reloadButtonHref) {
    // Check if it's an absolute URL (has protocol)
    if (reloadButtonHref.startsWith("http://") || reloadButtonHref.startsWith("https://")) {
      // Navigate to absolute URL - no reload needed, navigation will happen
      window.location.assign(reloadButtonHref);
      return;
    }

    // For hash routing: preserve the pathname and set/update the hash
    const url = new URL(window.location.href);
    url.searchParams.set("reloadedAt", String(Date.now() / 1000));
    url.hash = reloadButtonHref.startsWith("#") ? reloadButtonHref : `#${reloadButtonHref}`;
    window.location.assign(url.toString());
    return;
  }

  const url = new URL(window.location.href);
  url.searchParams.set("reloadedAt", String(Date.now() / 1000));
  window.location.assign(url.toString());
}

function findApiResponseError(error: unknown): APIResponseError | null {
  let currentError = error;

  for (let i = 0; i < 6 && currentError && typeof currentError === "object"; i++) {
    if (isApiResponseError(currentError)) {
      return currentError;
    }

    currentError = "cause" in currentError ? currentError.cause : null;
  }

  return null;
}

function errorTypeFromError(error: Error): ProgrammerReadableErrorType {
  const apiError = findApiResponseError(error);
  const errorToCheck = apiError ?? error;

  if (isNoNetworkError(errorToCheck)) {
    return "no-network";
  }

  if (isEmptyResponseBodyError(errorToCheck)) {
    return "no-network";
  }

  if (isRawNetworkError(errorToCheck)) {
    return "no-network";
  }

  if (is307RequestBlocked(errorToCheck)) {
    return "request-blocked";
  }

  if (isModuleLoadError(errorToCheck)) {
    return "module-load-error";
  }

  if (isLoadSheddingError(errorToCheck)) {
    return "service-unavailable-load-shedding";
  }

  if (apiError) {
    const status = apiError.response.status as HttpStatusCode;
    return errorTypeFromHttpStatus(status);
  }
  return "default";
}

export const ErrorPage: FC<ErrorPageProps> = ({
  error,
  errorType,
  children,
  reloadButtonText = "Reload page",
  noReloadButton,
  reloadButtonHref,
  showStackTraceInProduction = false,
  appBrokenSource = "error_boundary",
}) => {
  const finalErrorType = resolveErrorType(error, errorType);

  useSideEffectOnMount(() => reportAppBroken(appBrokenSource, error, finalErrorType));

  // Get configuration for this error type
  const config = getErrorConfig(finalErrorType);

  // Determine if reload button should be shown
  const showReloadButton = typeof noReloadButton !== "undefined" ? !noReloadButton : config.showReloadButton;

  return (
    <div className={styles.container} aria-label={autoTranslate("Error page")}>
      <div className={styles.content}>
        <div className={styles.imageContainer}>
          {config.svgUrl ? (
            <img src={config.svgUrl} alt={autoTranslate("Error illustration")} className={styles.image} />
          ) : (
            <ErrorMojo className={styles.image} />
          )}
        </div>
        {children ?? (
          <>
            <DDSHeadlineText level="md">{config.title}</DDSHeadlineText>
            <DDSBodyText>{config.message}</DDSBodyText>
          </>
        )}
        {(Config.nodeEnv !== "production" || showStackTraceInProduction) && error instanceof Error ? (
          <div>
            <DDSHeadlineText level="sm">{error.message as TranslatedString}</DDSHeadlineText>
            <pre className={errorBoundaryStyles.stackTrace}>
              <code>{error.stack}</code>
            </pre>
          </div>
        ) : null}
        {showReloadButton && (
          <DDSButton onClick={() => handleReloadButtonClick(reloadButtonHref)} data-name={"error-page.reload"}>
            {reloadButtonText}
          </DDSButton>
        )}
      </div>
    </div>
  );
};
